<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useThemeVars } from 'naive-ui';
import { useDebounceFn } from '@vueuse/core';
import { IconDownload } from '@/components/design/icons';
import {
  PlaybookRunEvaluationExportFormat,
  PlaybookRunStatus,
  PlaybookRunSummaryExportFormat,
  type PlaybookEvaluationGroupInDb,
  type PlaybookRunInDb,
  type UncommonsFinding,
} from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import {
  buildOptionMap,
  evaluationTypeLabel,
  findingSeverityBucket,
  findingThresholdTriggers,
  firedGroupActions,
  groupDisplayName,
  isUncommonsResult,
  latestResultForEvaluation,
  resultSeverity,
  runInputText,
  severityBucketRank,
  startedByLabel,
  uncommonsFindings,
  EVALUATION_TYPE_ORDER,
  SEVERITY_ORDER,
  type FiredGroupAction,
  type PlaybookEvaluation,
  type PlaybookEvaluationOption,
  type PlaybookEvaluationResult,
  type SeverityBucket,
} from '@/services/playbooks/playbookRunDisplay';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import EvaluationResultCard from '@/components/playbooks/run-results/EvaluationResultCard.vue';
import GroupActionIcons from '@/components/playbooks/run-results/GroupActionIcons.vue';
import UncommonsFindingBody from '@/components/playbooks/run-results/UncommonsFindingBody.vue';
import UncommonsTriggerActionsBody from '@/components/playbooks/run-results/UncommonsTriggerActionsBody.vue';
import { usePlaybookRunWebSocket } from '@/composables/usePlaybookRunWebSocket';
import { ContractFileDownloadService } from '@/services/contractManagement/contractFileDownloadService';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCollapse from '@/components/design/base/GaiaCollapse.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaResultsPanel from '@/components/design/composite/GaiaResultsPanel.vue';
import GaiaRiskPill, { type GaiaRiskLevel } from '@/components/design/base/GaiaRiskPill.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaSplit from '@/components/design/base/GaiaSplit.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaSummaryCard from '@/components/design/review/GaiaSummaryCard.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { playbookRunStatusMap, taskStatusMap } from '@/components/design/statusMaps';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaEnumSelect from '@/components/design/base/selects/GaiaEnumSelect.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { playbookRunsRoute } from '@/router/links';

const route = useRoute();
const message = useGaiaToast();
const themeVars = useThemeVars();

// The split fills what the shell leaves: the 56px topbar, 32px of Layout
// padding top and bottom, the 77px title block (naive's 30px h1 on the 1.6 body
// leading, then a 7px gap and the meta line), and the 14px root n-flex gap.
const splitHeight = 'calc(100vh - 211px)';

const playbookUid = computed<string>(() => {
  const uid = route.params.uid;
  return typeof uid === 'string' ? uid : '';
});
const runUid = computed<string>(() => {
  const uid = route.params.runUid;
  return typeof uid === 'string' ? uid : '';
});

const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const run = ref<PlaybookRunInDb | null>(null);
/** Breadcrumb crumb only — the run payload carries no playbook name. */
const playbookName = ref<string>('');
const evaluations = ref<PlaybookEvaluation[]>([]);
const evaluationGroups = ref<PlaybookEvaluationGroupInDb[]>([]);
const evaluationGroupsError = ref<string | null>(null);

const optionMap = computed<Map<string, PlaybookEvaluationOption>>(() =>
  buildOptionMap(evaluations.value)
);

type ResultEntry =
  | {
      kind: 'evaluation';
      evaluation: PlaybookEvaluation;
      result: PlaybookEvaluationResult;
      severity: SeverityBucket;
    }
  | {
      kind: 'finding';
      evaluation: PlaybookEvaluation;
      result: PlaybookEvaluationResult;
      finding: UncommonsFinding;
      index: number;
      total: number;
      severity: SeverityBucket;
    }
  | {
      kind: 'uncommons-actions';
      evaluation: PlaybookEvaluation;
      result: PlaybookEvaluationResult;
      severity: SeverityBucket;
    };

/** Unique collapse name per entry — findings share a result uid, so index disambiguates. */
function entryName(entry: ResultEntry): string {
  if (entry.kind === 'finding') return `${entry.result.uid}:${entry.index}`;
  if (entry.kind === 'uncommons-actions') return `${entry.result.uid}:actions`;
  return entry.result.uid;
}

function entryTitle(entry: ResultEntry): string {
  if (entry.kind === 'uncommons-actions') return 'Triggered actions';
  if (entry.kind === 'finding' && entry.total > 1) {
    return `${entry.evaluation.name} (${entry.index + 1})`;
  }
  return entry.evaluation.name;
}

/**
 * One latest result per evaluation that has a result. Uncommons results are exploded into one
 * entry per finding (each carrying its own severity); every other result stays a single entry.
 */
const resultEntries = computed<ResultEntry[]>(() => {
  const currentRun = run.value;
  if (!currentRun) return [];
  const entries: ResultEntry[] = [];
  for (const evaluation of evaluations.value) {
    const result = latestResultForEvaluation(currentRun, evaluation.uid);
    if (!result) continue;
    if (isUncommonsResult(result)) {
      const findings = uncommonsFindings(result);
      for (const [index, finding] of findings.entries()) {
        entries.push({
          kind: 'finding',
          evaluation,
          result,
          finding,
          index,
          total: findings.length,
          severity: findingSeverityBucket(finding.severity),
        });
      }
      if (findingThresholdTriggers(evaluation).length > 0) {
        entries.push({
          kind: 'uncommons-actions',
          evaluation,
          result,
          severity: 'Other',
        });
      }
      continue;
    }
    entries.push({
      kind: 'evaluation',
      evaluation,
      result,
      severity: resultSeverity(result, optionMap.value),
    });
  }
  return entries;
});

type SortMode = 'severity' | 'type' | 'groups';
const sortMode = ref<SortMode>('severity');

/** Severity bucket → risk pill. "Other" carries no risk, so it shows no pill. */
const severityRiskLevel: Partial<Record<SeverityBucket, GaiaRiskLevel>> = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

// Groups drops out when the group list failed to load — there is nothing to
// sort by then, and `load()` has already moved the mode back to severity.
const sortOptions = computed<GaiaSegmentedOption[]>(() => [
  { label: 'Severity', value: 'severity' },
  { label: 'Type', value: 'type' },
  ...(evaluationGroupsError.value ? [] : [{ label: 'Groups', value: 'groups' }]),
]);

type EntryGroup = {
  key: string;
  title: string;
  color: SeverityBucket | null;
  entries: ResultEntry[];
};

function groupedEntryName(group: EntryGroup, entry: ResultEntry): string {
  return `${group.key}:${entryName(entry)}`;
}

const groupedEntries = computed<EntryGroup[]>(() => {
  if (sortMode.value === 'type') {
    return EVALUATION_TYPE_ORDER.map(type => ({
      key: type,
      title: evaluationTypeLabel(type),
      color: null,
      entries: resultEntries.value
        .filter(entry => entry.evaluation.evaluation_type === (type as string))
        .sort((a, b) => severityBucketRank(a.severity) - severityBucketRank(b.severity)),
    })).filter(group => group.entries.length > 0);
  }
  if (sortMode.value === 'groups') {
    const bySeverity = (a: ResultEntry, b: ResultEntry): number =>
      severityBucketRank(a.severity) - severityBucketRank(b.severity);
    const groups: EntryGroup[] = evaluationGroups.value
      .map(group => {
        const memberUids = new Set(group.evaluation_uids ?? []);
        return {
          key: group.uid,
          title: groupDisplayName(group),
          color: null,
          entries: resultEntries.value
            .filter(entry => memberUids.has(entry.evaluation.uid))
            .sort(bySeverity),
        };
      })
      .filter(group => group.entries.length > 0);
    // Results whose evaluation isn't in any group, so nothing is hidden in this view.
    const groupedUids = new Set(
      evaluationGroups.value.flatMap(group => group.evaluation_uids ?? [])
    );
    const ungrouped = resultEntries.value
      .filter(entry => !groupedUids.has(entry.evaluation.uid))
      .sort(bySeverity);
    if (ungrouped.length > 0) {
      groups.push({ key: '__ungrouped__', title: 'Ungrouped', color: null, entries: ungrouped });
    }
    return groups;
  }
  return SEVERITY_ORDER.map(bucket => ({
    key: bucket,
    title: bucket,
    color: bucket,
    entries: resultEntries.value.filter(entry => entry.severity === bucket),
  })).filter(group => group.entries.length > 0);
});

/**
 * Group-triggered actions that ran, keyed by group. They belong to the group, not
 * to whichever member evaluation recorded them, so they surface on the group header
 * rather than inside a result card.
 */
const firedActionsByGroupUid = computed<Map<string, FiredGroupAction[]>>(() => {
  const byGroupUid = new Map<string, FiredGroupAction[]>();
  const fired = run.value ? firedGroupActions(run.value, evaluationGroups.value) : [];
  for (const entry of fired) {
    const bucket = byGroupUid.get(entry.group.uid) ?? [];
    bucket.push(entry);
    byGroupUid.set(entry.group.uid, bucket);
  }
  return byGroupUid;
});

const hasSummary = computed<boolean>(() => Boolean(run.value?.markdown_summary?.trim()));
const isRunning = computed<boolean>(
  () =>
    run.value?.status === PlaybookRunStatus.RUNNING ||
    run.value?.status === PlaybookRunStatus.SUMMARIZING
);

const revision = computed(() => run.value?.input_vault_file_revision ?? null);
const fileName = computed<string>(() => revision.value?.file_name ?? 'Playbook run');
/** The meta line under the title: what ran, how much came back, and when. */
const resultCount = computed<number>(() => resultEntries.value.length);
const startedAt = computed<string>(() =>
  formatDateForDisplay(run.value?.started_at ?? run.value?.created_at)
);
const startedBy = computed<string>(() => (run.value ? startedByLabel(run.value) : ''));
const inputText = computed<string | null>(() => (run.value ? runInputText(run.value) : null));

const redlinedRevision = computed(() => run.value?.output_vault_file_revision ?? null);
const downloading = ref<boolean>(false);

async function downloadRedlined(): Promise<void> {
  const rev = redlinedRevision.value;
  if (!rev) return;
  downloading.value = true;
  try {
    const blob = await ContractFileDownloadService.downloadVaultFile(rev.uid, rev.mime_type);
    ContractFileDownloadService.triggerDownload(blob, rev.file_name);
  } catch (error_) {
    message.error(
      error_ instanceof Error ? error_.message : 'Failed to download red-lined document.'
    );
  } finally {
    downloading.value = false;
  }
}

type PlaybookExportKind = 'summary' | 'evaluations';
const showExportModal = ref<boolean>(false);
/** A run without a summary can only export its evaluations. */
const exportKindOptions = computed(() => [
  ...(hasSummary.value ? [{ label: 'Summary', value: 'summary' }] : []),
  { label: 'Evaluations', value: 'evaluations' },
]);

/** Export formats read as file types here, not as the enum's bare names. */
const SUMMARY_FORMAT_LABELS: Record<string, string> = {
  [PlaybookRunSummaryExportFormat.PDF]: 'PDF',
  [PlaybookRunSummaryExportFormat.MD]: 'Markdown',
};
const EVALUATION_FORMAT_LABELS: Record<string, string> = {
  [PlaybookRunEvaluationExportFormat.XLSX]: 'Excel (XLSX)',
  [PlaybookRunEvaluationExportFormat.CSV]: 'CSV',
};

const exportKind = ref<PlaybookExportKind>('summary');
const summaryExportFormat = ref<PlaybookRunSummaryExportFormat>(PlaybookRunSummaryExportFormat.PDF);
const evaluationsExportFormat = ref<PlaybookRunEvaluationExportFormat>(
  PlaybookRunEvaluationExportFormat.XLSX
);
const exporting = ref<boolean>(false);

/** One entry per evaluation that has a result, deduplicated (uncommons evaluations fan out to
 * several rows in the results list, but they all export as a single evaluation). */
const evaluationExportOptions = computed<{ label: string; value: string }[]>(() => {
  const seen = new Set<string>();
  const options: { label: string; value: string }[] = [];
  for (const entry of resultEntries.value) {
    if (seen.has(entry.evaluation.uid)) continue;
    seen.add(entry.evaluation.uid);
    options.push({ label: entry.evaluation.name, value: entry.evaluation.uid });
  }
  return options;
});
const selectedEvaluationUids = ref<string[]>([]);

const baseFileName = computed<string>(() => fileName.value.replace(/\.[^./]+$/, ''));

function openExportModal(): void {
  exportKind.value = hasSummary.value ? 'summary' : 'evaluations';
  selectedEvaluationUids.value = evaluationExportOptions.value.map(option => option.value);
  showExportModal.value = true;
}

function closeExportModal(): void {
  showExportModal.value = false;
  exporting.value = false;
}

async function runExport(): Promise<void> {
  if (!runUid.value) return;
  if (exportKind.value === 'evaluations' && selectedEvaluationUids.value.length === 0) {
    message.error('Select at least one evaluation to export.');
    return;
  }
  exporting.value = true;
  try {
    if (exportKind.value === 'summary') {
      const blob = await PlaybooksService.exportRunSummary(runUid.value, summaryExportFormat.value);
      ContractFileDownloadService.triggerDownload(
        blob,
        `${baseFileName.value}_summary.${summaryExportFormat.value}`
      );
    } else {
      const uids =
        selectedEvaluationUids.value.length === evaluationExportOptions.value.length
          ? undefined
          : selectedEvaluationUids.value;
      const blob = await PlaybooksService.exportRunEvaluations(
        runUid.value,
        evaluationsExportFormat.value,
        uids
      );
      ContractFileDownloadService.triggerDownload(
        blob,
        `${baseFileName.value}_evaluations.${evaluationsExportFormat.value}`
      );
    }
    closeExportModal();
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to export.');
  } finally {
    exporting.value = false;
  }
}

// PDF preview + highlight wiring (mirrors ReviewDetailView).
const pdfKey = ref<number>(0);
const handleSplitResize = useDebounceFn((): void => {
  pdfKey.value += 1;
}, 200);
const pdfPreviewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();

function showInPdf(payload: { page: number; texts: string[] }): void {
  if (!pdfPreviewRef.value) return;
  pdfPreviewRef.value.goToPage(payload.page);
  pdfPreviewRef.value.setHighlightText(payload.texts);
}

function handleExpand(names: (string | number)[]): void {
  const openName = names.at(-1);
  if (openName === undefined) return;
  let entry: ResultEntry | undefined;
  for (const group of groupedEntries.value) {
    entry = group.entries.find(
      candidate => groupedEntryName(group, candidate) === String(openName)
    );
    if (entry) break;
  }
  if (!entry) return;
  if (entry.kind === 'finding') {
    showInPdf({
      page: entry.finding.page_number ?? 1,
      texts: entry.finding.text ? [entry.finding.text] : [],
    });
    return;
  }
  showInPdf({
    page: entry.result.page_number ?? 1,
    texts: entry.result.highlighted_text ?? [],
  });
}

type EvaluationGroupsLoadResult = {
  groups: PlaybookEvaluationGroupInDb[];
  error: string | null;
};

async function loadEvaluationGroups(): Promise<EvaluationGroupsLoadResult> {
  if (!playbookUid.value) return { groups: [], error: null };
  try {
    return {
      groups: await PlaybooksService.listEvaluationGroups(playbookUid.value),
      error: null,
    };
  } catch (error_) {
    return {
      groups: [],
      error: error_ instanceof Error ? error_.message : 'Failed to load evaluation groups.',
    };
  }
}

async function loadPlaybookName(): Promise<void> {
  if (!playbookUid.value) return;
  try {
    const playbook = await PlaybooksService.getById(playbookUid.value);
    playbookName.value = playbook.name;
  } catch {
    playbookName.value = '';
  }
}

async function load(): Promise<void> {
  if (!runUid.value) return;
  loading.value = true;
  error.value = null;
  evaluationGroupsError.value = null;
  try {
    const [fetchedRun, fetchedEvaluations, fetchedGroups] = await Promise.all([
      PlaybooksService.getRun(runUid.value),
      playbookUid.value
        ? PlaybooksService.listEvaluations(playbookUid.value)
        : Promise.resolve([] as PlaybookEvaluation[]),
      loadEvaluationGroups(),
    ]);
    run.value = fetchedRun;
    evaluations.value = fetchedEvaluations;
    evaluationGroups.value = fetchedGroups.groups;
    evaluationGroupsError.value = fetchedGroups.error;
    if (fetchedGroups.error && sortMode.value === 'groups') {
      sortMode.value = 'severity';
    }
  } catch (error_) {
    error.value = error_ instanceof Error ? error_.message : 'Failed to load playbook run.';
  } finally {
    loading.value = false;
  }
}

/** Refetch only the run (evaluations don't change per run) on a websocket update. */
async function reloadRun(): Promise<void> {
  if (!runUid.value) return;
  try {
    run.value = await PlaybooksService.getRun(runUid.value);
  } catch {
    // Keep the current run on a transient refetch error.
  }
}

usePlaybookRunWebSocket({
  runUids: () => (runUid.value ? [runUid.value] : []),
  onRunUpdated: () => void reloadRun(),
});

onMounted(() => {
  void load();
  void loadPlaybookName();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'playbooks' }">{{ navLabels.playbooks }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="playbookRunsRoute(playbookUid)">
          {{ playbookName || 'Runs' }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ fileName }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="14">
      <!-- Title, the run's own facts, and what you can take away from it. -->
      <n-flex align="flex-start" :size="16">
        <n-flex vertical :size="7" :style="{ flex: '1 1 300px', minWidth: 0 }">
          <gaia-header :level="1" :style="{ minWidth: 0 }">{{ fileName }}</gaia-header>
          <n-flex align="center" :size="9" :wrap="false">
            <gaia-status-indicator
              v-if="run"
              :status="playbookRunStatusMap[run.status].status"
              :glyph="playbookRunStatusMap[run.status].glyph"
            >
              {{ playbookRunStatusMap[run.status].label }}
            </gaia-status-indicator>
            <gaia-text size="meta" tone="faint">·</gaia-text>
            <gaia-text size="meta" tone="subtle">
              {{ resultCount }} {{ resultCount === 1 ? 'result' : 'results' }}
            </gaia-text>
            <gaia-text size="meta" tone="faint">·</gaia-text>
            <gaia-text size="meta" tone="subtle">
              Started {{ startedAt }}<template v-if="startedBy"> by {{ startedBy }}</template>
            </gaia-text>
          </n-flex>
        </n-flex>

        <n-flex
          align="center"
          :size="8"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-button v-if="redlinedRevision" :loading="downloading" @click="downloadRedlined">
            <template #icon>
              <n-icon :component="IconDownload" />
            </template>
            Red-lined document
          </gaia-button>
          <gaia-button type="primary" @click="openExportModal">
            <template #icon>
              <n-icon :component="IconDownload" />
            </template>
            Export
          </gaia-button>
        </n-flex>
      </n-flex>

      <gaia-banner v-if="error">{{ error }}</gaia-banner>
      <gaia-banner v-if="evaluationGroupsError" type="warning">
        Evaluation groups could not be loaded, so group sorting is unavailable.
        {{ evaluationGroupsError }}
      </gaia-banner>

      <n-spin :show="loading" style="flex: 1; min-height: 0">
        <gaia-split
          :style="{ height: splitHeight }"
          :min="0.2"
          :max="0.8"
          :default-size="0.5"
          @update:size="handleSplitResize"
        >
          <template #1>
            <div
              data-tour="pb.run-results"
              :style="{ height: '100%', overflow: 'auto', paddingRight: '16px' }"
            >
              <n-flex vertical :size="12" :wrap="false">
                <gaia-summary-card v-if="run">
                  <markdown-renderer v-if="hasSummary" :content="run.markdown_summary ?? ''" />
                  <n-flex v-else-if="isRunning" align="center" :size="8">
                    <gaia-spinner :size="14" />
                    <gaia-text size="body" tone="subtle">Still generating…</gaia-text>
                  </n-flex>
                  <gaia-text v-else size="body" tone="warning">
                    Something went wrong generating the summary.
                  </gaia-text>
                </gaia-summary-card>

                <gaia-results-panel title="Results" :count="resultCount">
                  <template v-if="groupedEntries.length" #actions>
                    <gaia-segmented
                      :value="sortMode"
                      :options="sortOptions"
                      @update:value="value => (sortMode = value as SortMode)"
                    />
                  </template>

                  <gaia-empty-state
                    v-if="!loading && groupedEntries.length === 0"
                    title="No evaluation results for this run."
                  />
                  <gaia-collapse v-else accordion @update:expanded-names="handleExpand">
                    <template v-for="group in groupedEntries" :key="group.key">
                      <gaia-section-header variant="strip" :count="String(group.entries.length)">
                        {{ group.title }}
                        <!-- Group-triggered actions that fired; hover an icon for what it did. -->
                        <template #extra>
                          <group-action-icons
                            :fired="firedActionsByGroupUid.get(group.key) ?? []"
                          />
                        </template>
                      </gaia-section-header>
                      <n-collapse-item
                        v-for="entry in group.entries"
                        :key="groupedEntryName(group, entry)"
                        :name="groupedEntryName(group, entry)"
                      >
                        <template #header>
                          <n-ellipsis :style="{ flex: 1, minWidth: 0 }">
                            {{ entryTitle(entry) }}
                          </n-ellipsis>
                        </template>
                        <template #header-extra>
                          <n-flex align="center" :size="8" :wrap="false">
                            <gaia-tag
                              v-if="sortMode === 'severity' || sortMode === 'groups'"
                              variant="muted"
                            >
                              {{ evaluationTypeLabel(entry.evaluation.evaluation_type) }}
                            </gaia-tag>
                            <!-- Severity rides every row, as the mock draws it: the
                                 group strip scrolls out of view, the pill does not. -->
                            <gaia-risk-pill
                              v-if="severityRiskLevel[entry.severity]"
                              pill
                              :level="severityRiskLevel[entry.severity]!"
                            >
                              {{ entry.severity }}
                            </gaia-risk-pill>
                            <gaia-tooltip>
                              <template #trigger>
                                <gaia-status-indicator
                                  :status="taskStatusMap[entry.result.status].status"
                                  :glyph="taskStatusMap[entry.result.status].glyph"
                                />
                              </template>
                              {{ taskStatusMap[entry.result.status].label }}
                            </gaia-tooltip>
                          </n-flex>
                        </template>
                        <uncommons-finding-body
                          v-if="entry.kind === 'finding'"
                          :finding="entry.finding"
                          @highlight="showInPdf"
                        />
                        <uncommons-trigger-actions-body
                          v-else-if="entry.kind === 'uncommons-actions'"
                          :evaluation="entry.evaluation"
                          :result="entry.result"
                        />
                        <evaluation-result-card
                          v-else
                          :evaluation="entry.evaluation"
                          :result="entry.result"
                          :option-map="optionMap"
                          @highlight="showInPdf"
                        />
                      </n-collapse-item>
                    </template>
                  </gaia-collapse>
                </gaia-results-panel>
              </n-flex>
            </div>
          </template>

          <template #2>
            <div data-tour="pb.run-preview" style="height: 100%; padding-left: 16px">
              <gaia-vault-file-pdf-preview
                v-if="revision"
                :key="pdfKey"
                ref="pdfPreviewRef"
                :revision-uid="revision.uid"
                :revision-preview-status="revision.preview_status ?? null"
              />
              <n-flex v-else-if="inputText" vertical :size="8" style="height: 100%">
                <gaia-section-header>Input text</gaia-section-header>
                <pre
                  :style="{
                    margin: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    padding: '12px',
                    background: themeVars.actionColor,
                    borderRadius: '6px',
                  }"
                  >{{ inputText }}</pre>
              </n-flex>
              <gaia-empty-state v-else title="No document for this run." />
            </div>
          </template>
        </gaia-split>
      </n-spin>
    </n-flex>

    <n-modal
      v-model:show="showExportModal"
      preset="card"
      title="Export Run"
      style="width: 480px"
      :mask-closable="false"
    >
      <n-form label-placement="top">
        <n-form-item label="Export">
          <gaia-select v-model:value="exportKind" :options="exportKindOptions" />
        </n-form-item>

        <n-form-item v-if="exportKind === 'summary'" label="Format">
          <gaia-enum-select
            v-model:value="summaryExportFormat"
            :enum-source="PlaybookRunSummaryExportFormat"
            :labels="SUMMARY_FORMAT_LABELS"
          />
        </n-form-item>

        <template v-else>
          <n-form-item label="Format">
            <gaia-enum-select
              v-model:value="evaluationsExportFormat"
              :enum-source="PlaybookRunEvaluationExportFormat"
              :labels="EVALUATION_FORMAT_LABELS"
            />
          </n-form-item>
          <n-form-item label="Evaluations to Include">
            <n-transfer
              v-model:value="selectedEvaluationUids"
              :options="evaluationExportOptions"
              style="width: 100%; height: 300px"
            />
          </n-form-item>
        </template>
      </n-form>

      <template #footer>
        <n-flex justify="end" :size="10">
          <n-button @click="closeExportModal">Cancel</n-button>
          <n-button type="primary" :loading="exporting" @click="runExport">Export</n-button>
        </n-flex>
      </template>
    </n-modal>
  </Layout>
</template>
